import {
  MaintenanceLogWithRelations,
  MaintenanceTaskWithSteps,
  PackageVersionTask,
} from '@markku/types'
import { groupBy, sum } from '@markku/utils'

import {
  getCurrentSeasonIndex,
  getMonthRange,
  getSeasonAndYearFromDeadline,
  getSeasonAndYearFromSchedulingType,
  getSeasonColor,
  getSeasonImage,
  getSeasonName,
  getTaskSeason,
  mapTaskToUI,
  orderTasksByCompletion,
  SeasonData,
  SEASON_ORDER,
} from './utils'

interface SeasonDataResult {
  seasons: SeasonData[]
  // Key format: "taskId-season-year" (e.g., "123-winter-2024")
  logsByTaskAndSeasonYear: Map<string, MaintenanceLogWithRelations>
  currentSeasonSlideIndex: number
}

interface BuildSeasonDataParams {
  tasks: MaintenanceTaskWithSteps[]
  logs: MaintenanceLogWithRelations[]
  packageTasks: PackageVersionTask[]
  propertyId: number
  hasPackage: boolean
}

// Pure function for transforming tasks/logs into season data
export const buildSeasonData = ({
  tasks,
  logs,
  packageTasks,
  propertyId,
  hasPackage,
}: BuildSeasonDataParams): SeasonDataResult => {
  // Filter logs with valid maintenanceTaskId for keying
  const logsWithTaskId = logs.filter(
    (log): log is MaintenanceLogWithRelations & { maintenanceTaskId: number } =>
      log.maintenanceTaskId !== null,
  )

  const logsBySeason = groupBy(logs, (log) =>
    log.completedDate
      ? getSeasonAndYearFromDeadline(log.completedDate).season
      : 'unknown',
  )

  const tasksBySeason = groupBy(tasks, getTaskSeason)

  const markkuTaskIds = new Set(packageTasks.map((pt) => pt.maintenanceTaskId))

  // Build maps for completed task IDs and logs by season-year
  // Key format: "season-year" (e.g., "summer-2025") for completedTaskIds
  // Key format: "taskId-season-year" (e.g., "123-winter-2024") for logs
  const completedTaskIdsBySeasonYear = new Map<string, Set<number>>()
  const logsByTaskAndSeasonYear = new Map<string, MaintenanceLogWithRelations>()

  for (const log of logsWithTaskId) {
    if (!log.completedDate) continue

    // Get the task to determine its season
    const task = tasks.find((t) => t.id === log.maintenanceTaskId)
    const isSystemTask = task?.propertyId === null

    // For system tasks, use the scheduling type to determine season (with winter adjustment)
    // For property tasks, use the deadline
    const { season, year } = isSystemTask
      ? getSeasonAndYearFromSchedulingType(
          log.completedDate,
          task?.schedulingType ?? null,
        )
      : getSeasonAndYearFromDeadline(log.completedDate)

    // Track completed task IDs per season-year
    const seasonYearKey = `${season}-${year}`
    if (!completedTaskIdsBySeasonYear.has(seasonYearKey)) {
      completedTaskIdsBySeasonYear.set(seasonYearKey, new Set())
    }
    completedTaskIdsBySeasonYear.get(seasonYearKey)!.add(log.maintenanceTaskId)

    // Store log by taskId-season-year for modal lookup
    const logKey = `${log.maintenanceTaskId}-${season}-${year}`
    logsByTaskAndSeasonYear.set(logKey, log)
  }

  // Build raw task data for each season (without completion status - that's year-dependent)
  const seasonRawDataMap = new Map(
    SEASON_ORDER.map((season) => {
      const seasonTasks = tasksBySeason.get(season) || []
      const seasonLogs = logsBySeason.get(season) || []

      const totalCost = sum(
        seasonLogs.map((log) =>
          log.actualCost ? parseFloat(log.actualCost) : 0,
        ),
      )

      // Filter tasks into categories (raw, not UI-mapped yet)
      const ownTasks = seasonTasks.filter((t) => t.propertyId === propertyId)

      const recommendedTasks = seasonTasks.filter(
        (t) =>
          t.propertyId === null &&
          !markkuTaskIds.has(t.id) &&
          t.name !== '__CUSTOM_TASK__',
      )

      const markkuTasks = hasPackage
        ? seasonTasks.filter(
            (t) =>
              t.propertyId === null &&
              markkuTaskIds.has(t.id) &&
              t.name !== '__CUSTOM_TASK__',
          )
        : []

      return [
        season,
        { ownTasks, recommendedTasks, markkuTasks, totalCost },
      ] as const
    }),
  )

  // Generate 8 seasons: 4 previous + current + 3 future
  const currentSeasonIdx = getCurrentSeasonIndex()
  // In Jan/Feb we're still in the previous year's winter, so adjust base year for season display
  const month = new Date().getMonth()
  const currentYear = new Date().getFullYear()
  const seasonBaseYear = month <= 1 ? currentYear - 1 : currentYear

  const seasons = Array.from({ length: 8 }, (_, i): SeasonData => {
    const offset = i - 4
    const idx = (currentSeasonIdx + offset + 8) % 4
    const season = SEASON_ORDER[idx]
    const rawData = seasonRawDataMap.get(season)!
    const year = seasonBaseYear + Math.floor((currentSeasonIdx + offset) / 4)

    // Get completed task IDs for this specific season-year
    const completedTaskIds =
      completedTaskIdsBySeasonYear.get(`${season}-${year}`) ?? new Set<number>()

    // Filter own tasks by year (they have specific deadlines)
    const yearFilteredOwnTasks = rawData.ownTasks.filter((task) => {
      if (!task.deadline) return true
      const { year: taskYear } = getSeasonAndYearFromDeadline(task.deadline)
      return taskYear === year
    })

    // Now map to UI format with the correct year-specific completion status
    const uiOwnTasks = orderTasksByCompletion(
      yearFilteredOwnTasks,
      completedTaskIds,
      (t) => new Date(t.createdAt),
    ).map((t) => mapTaskToUI(t, completedTaskIds, true))

    const uiRecommendedTasks = orderTasksByCompletion(
      rawData.recommendedTasks,
      completedTaskIds,
      (t) => t.name,
    ).map((t) => mapTaskToUI(t, completedTaskIds, true))

    const uiMarkkuTasks = rawData.markkuTasks.map((t) =>
      mapTaskToUI(t, completedTaskIds, false),
    )

    const allSeasonTasks = [
      ...uiOwnTasks,
      ...uiRecommendedTasks,
      ...uiMarkkuTasks,
    ]

    return {
      season,
      seasonName: getSeasonName(season),
      year,
      tasks: uiOwnTasks,
      recommendedTasks: uiRecommendedTasks,
      markkuTasks: uiMarkkuTasks,
      completedCount: allSeasonTasks.filter((t) => t.completed).length,
      totalCount: allSeasonTasks.length,
      monthRange: getMonthRange(season),
      backgroundImage: getSeasonImage(season),
      backgroundColor: getSeasonColor(season),
      totalCost: rawData.totalCost,
    }
  })

  return {
    seasons,
    logsByTaskAndSeasonYear,
    currentSeasonSlideIndex: 4,
  }
}
